import React from 'react'
import { Link } from 'react-router-dom'
import {
  Activity,
  ArrowRight,
  BookOpenCheck,
  Brain,
  Building2,
  CheckCircle2,
  ClipboardList,
  CloudDrizzle,
  Cpu,
  GraduationCap,
  HeartPulse,
  LineChart,
  MapPin,
  Microscope,
  Puzzle,
  Repeat,
  ShieldCheck,
  Stethoscope,
  UserCheck,
  Users,
  Video,
  Waves,
  Workflow,
  Zap,
} from './icons'

// Shared primitives

export const Container = ({ className = '', children }) => (
  <div className={`as-container ${className}`}>{children}</div>
)

export const Eyebrow = ({ children, dark = false }) => (
  <div className={`as-eyebrow${dark ? ' as-eyebrow--dark' : ''}`}>{children}</div>
)

export const SectionTitle = ({ children, as: As = 'h2', className = '' }) => (
  <As className={`as-h2 ${className}`}>{children}</As>
)

export const Lede = ({ children, className = '' }) => (
  <p className={`as-lede ${className}`}>{children}</p>
)

export const PrimaryButton = ({ to, href, children, className = '' }) =>
  to ? (
    <Link to={to} className={`as-btn as-btn-primary ${className}`}>
      {children}
      <ArrowRight />
    </Link>
  ) : (
    <a href={href} className={`as-btn as-btn-primary ${className}`}>
      {children}
      <ArrowRight />
    </a>
  )

export const SecondaryButton = ({ to, href, children, className = '', onClick }) => {
  const cls = `as-btn as-btn-secondary ${className}`
  if (onClick)
    return (
      <button type="button" onClick={onClick} className={cls}>
        {children}
      </button>
    )
  if (to)
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    )
  return (
    <a href={href} className={cls}>
      {children}
    </a>
  )
}

export const Card = ({ children, className = '', emphasised = false }) => (
  <div className={`as-card${emphasised ? ' as-card-emphasised' : ''} ${className}`}>
    {children}
  </div>
)

export const IconBadge = ({ Icon, variant = 'violet' }) => {
  const cls =
    variant === 'slate'
      ? 'as-icon-badge as-icon-badge--slate'
      : variant === 'dark'
        ? 'as-icon-badge as-icon-badge--dark'
        : 'as-icon-badge'
  return (
    <div className={cls}>
      <Icon />
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// HOME
// ────────────────────────────────────────────────────────────

// Decision-first hero: a primary path for unsure visitors (Get Help) and a
// secondary path for visitors who already know what they need.
export const Hero = () => (
  <section className="as-hero" aria-labelledby="hero-title">
    <Container>
      <div className="as-hero-grid">
        <div>
          <Eyebrow>Clinical mental healthcare</Eyebrow>
          <h1 id="hero-title" className="as-h1">
            Clinical mental healthcare,{' '}
            <span className="as-brand-gradient">with clear next steps</span>.
          </h1>
          <p className="as-lede">
            Not sure what you need? Start here — we&rsquo;ll guide you to the right kind of
            care, whether that&rsquo;s therapy, a clinical consultation, or a fuller workup.
          </p>
          <div className="as-mt-8">
            <PrimaryButton to="/care/start">Get help</PrimaryButton>
          </div>
          <p className="as-mt-6 as-p" style={{ color: 'var(--slate-500)' }}>
            Already know what you&rsquo;re looking for?
          </p>
          <div className="as-mt-3 as-flex as-wrap as-items-center as-gap-3">
            <SecondaryButton to="/therapy-psychiatry?t=psychiatrist">Book consultation</SecondaryButton>
            <SecondaryButton to="/therapy-psychiatry?t=therapist">Start therapy</SecondaryButton>
            <Link to="/how-care-works" className="as-link">
              How care works →
            </Link>
          </div>
        </div>
        <div>
          <div className="as-pathway" aria-label="Where to start">
            <p className="as-pathway-label">Where to start</p>
            <ol className="as-pathway-list">
              {[
                { Icon: UserCheck, title: 'Tell us a little', sub: 'A short intake to surface what kind of care fits.' },
                { Icon: Puzzle, title: 'Get matched', sub: 'A therapist, psychiatrist, or both — based on need, not availability.' },
                { Icon: Repeat, title: 'Stay supported', sub: 'Plans adjust as things change. The care team stays with you.' },
              ].map(({ Icon, title, sub }) => (
                <li key={title} className="as-pathway-item">
                  <div className="as-icon-circle">
                    <Icon />
                  </div>
                  <div>
                    <p className="as-pathway-item-title">{title}</p>
                    <p className="as-pathway-item-sub">{sub}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="as-pathway-footer">
              <Workflow /> A care team that talks to itself, so you don&rsquo;t have to.
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
)

export const TrustStrip = () => (
  <section aria-label="Trust signals" className="as-trust">
    <Container>
      <ul className="as-grid-trust">
        {[
          { Icon: ShieldCheck, label: 'Clinic-integrated care' },
          { Icon: Stethoscope, label: 'Psychiatrist-guided approach' },
          { Icon: ClipboardList, label: 'Structured therapy delivery' },
          { Icon: MapPin, label: 'Accessible across regions' },
        ].map(({ Icon, label }) => (
          <li key={label} className="as-trust-item">
            <Icon /> <span>{label}</span>
          </li>
        ))}
      </ul>
    </Container>
  </section>
)

// ────────────────────────────────────────────────────────────
// GET STARTED — decision-first entry points
// ────────────────────────────────────────────────────────────

const STARTING_POINTS = [
  {
    Icon: CloudDrizzle,
    title: 'I\u2019m feeling anxious, low, or overwhelmed',
    body: 'Start by understanding what may be going on.',
    cta: 'Understand possible concerns',
    to: '/assessment',
  },
  {
    Icon: Users,
    title: 'I want to talk to someone',
    body: 'Begin with a therapist matched to what you\u2019re working through.',
    cta: 'Start therapy',
    to: '/therapy-psychiatry?t=therapist',
  },
  {
    Icon: Stethoscope,
    title: 'I think I may need medical evaluation or medication',
    body: 'Speak with a psychiatrist for a clinical assessment.',
    cta: 'Book consultation',
    to: '/therapy-psychiatry?t=psychiatrist',
  },
  {
    Icon: Workflow,
    title: 'I\u2019m not sure what I need',
    body: 'Our team will help figure out the right starting point with you.',
    cta: 'Get a guided recommendation',
    to: '/care/start',
  },
]

export const GetStartedSection = () => (
  <section className="as-section as-section-white" aria-labelledby="get-started-heading">
    <Container>
      <div className="as-max-w-3">
        <Eyebrow>Get started</Eyebrow>
        <SectionTitle>
          <span id="get-started-heading">Choose a starting point</span>
        </SectionTitle>
        <Lede>
          Mental healthcare can feel unclear at the beginning. Pick whatever feels closest to
          where you are right now &mdash; we&rsquo;ll take it from there.
        </Lede>
      </div>
      <ul className="as-mt-12 as-grid as-grid-2">
        {STARTING_POINTS.map(({ Icon, title, body, cta, to }) => (
          <li key={title}>
            <Card>
              <div className="as-flex as-items-start as-gap-4">
                <IconBadge Icon={Icon} />
                <div>
                  <h3 className="as-h3">{title}</h3>
                  <p className="as-p as-mt-2">{body}</p>
                  <div className="as-mt-4">
                    <Link to={to} className="as-link as-link-violet">
                      {cta} <ArrowRight />
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </Container>
  </section>
)

// ────────────────────────────────────────────────────────────
// REAL-WORLD SCENARIOS — patient quotes, not frameworks
// ────────────────────────────────────────────────────────────

const SCENARIOS = [
  {
    quote: 'I\u2019ve been feeling anxious and unable to sleep.',
    response: 'Start with therapy and a sleep-focused review.',
    to: '/therapy-psychiatry?t=therapist',
  },
  {
    quote: 'I\u2019ve tried therapy but things aren\u2019t improving.',
    response: 'Bring a clinical consultation into your plan.',
    to: '/therapy-psychiatry?t=psychiatrist',
  },
  {
    quote: 'I feel low, tired, and not like myself.',
    response: 'Look at psychological and biological factors together.',
    to: '/how-care-works',
  },
  {
    quote: 'I\u2019m not sure what\u2019s happening.',
    response: 'Begin with a guided clinical intake.',
    to: '/care/start',
  },
]

export const RealWorldScenarios = () => (
  <section className="as-section as-section-alt" aria-labelledby="scenarios-heading">
    <Container>
      <div className="as-max-w-3">
        <Eyebrow>Real-world use cases</Eyebrow>
        <SectionTitle>
          <span id="scenarios-heading">When people come to AUMHUM</span>
        </SectionTitle>
        <Lede>
          Most people don&rsquo;t arrive with a diagnosis &mdash; they arrive with a feeling.
          Here&rsquo;s how that usually maps to a starting plan.
        </Lede>
      </div>
      <ul className="as-mt-12 as-grid as-grid-2">
        {SCENARIOS.map(({ quote, response, to }) => (
          <li key={quote}>
            <Card className="as-scenario-card">
              <p className="as-scenario-quote">&ldquo;{quote}&rdquo;</p>
              <div className="as-scenario-arrow" aria-hidden="true">
                <ArrowRight />
              </div>
              <p className="as-scenario-response">{response}</p>
              <div className="as-mt-4">
                <Link to={to} className="as-link as-link-violet">
                  Explore this path <ArrowRight />
                </Link>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </Container>
  </section>
)

export const WhatWeDo = () => (
  <section className="as-section as-section-white">
    <Container>
      <div className="as-max-w-3">
        <Eyebrow>What we do</Eyebrow>
        <SectionTitle>Mental healthcare, beyond isolated sessions</SectionTitle>
        <Lede>
          Care often exists in fragments &mdash; a consultation here, a session there. AUMHUM
          joins these into one care process, where evaluation, therapy, and follow-up move
          together.
        </Lede>
        <Lede>
          Care may involve psychological support, psychiatric and medical care, and biological
          considerations &mdash; brought in only when they help.
        </Lede>
      </div>
    </Container>
  </section>
)

export const CompleteCare = () => {
  const team = [
    { Icon: Users, title: 'Therapists', body: 'Licensed psychologists delivering structured therapy — CBT, REBT, trauma-focused, and family work — matched to clinical need.' },
    { Icon: Stethoscope, title: 'Psychiatrists', body: 'Diagnostic clarity, medication when clinically indicated, and ongoing psychiatric review — integrated with your therapy plan.' },
    { Icon: Microscope, title: 'Endocrinologists & physicians', body: 'Thyroid, reproductive, metabolic, and sleep workups where symptoms suggest a biological driver. Medical care, not just talk therapy.' },
    { Icon: Workflow, title: 'Clinical coordination', body: "A care team that communicates internally — so you aren't the one stitching specialists together." },
  ]
  return (
    <section className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Complete care</Eyebrow>
          <SectionTitle>
            Therapists, psychiatrists, and medical specialists — working as one care team
          </SectionTitle>
          <Lede>
            Mental health is rarely one-dimensional. Effective care often requires psychological
            support, psychiatric evaluation, and medical review — all coordinated so nothing
            falls between the cracks.
          </Lede>
        </div>
        <div className="as-mt-12 as-grid as-grid-2">
          {team.map(({ Icon, title, body }) => (
            <Card key={title}>
              <IconBadge Icon={Icon} />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </Card>
          ))}
        </div>
      </Container>
    </section>
  )
}

export const GuidedCare = () => {
  const items = [
    { Icon: UserCheck, title: 'Find the right starting point', body: 'A short intake helps identify whether therapy, a psychiatric review, or a fuller workup fits best.' },
    { Icon: Puzzle, title: 'Match to appropriate care', body: 'A clinician picked for your situation \u2014 not a directory you have to search.' },
    { Icon: Repeat, title: 'Adjust as things change', body: 'Plans get revisited as the picture changes; nothing is set in stone.' },
    { Icon: Workflow, title: 'Continuity over time', body: 'Therapist, psychiatrist, and physician work from one clinical view of you.' },
  ]
  return (
    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Guided care</Eyebrow>
          <SectionTitle>Knowing what to do next</SectionTitle>
          <Lede>
            Access on its own isn&rsquo;t enough. Most people need direction too &mdash;
            understanding symptoms, choosing the right support, and continuing care over time.
            AUMHUM guides each step.
          </Lede>
        </div>
        <dl className="as-mt-12 as-grid as-grid-2">
          {items.map(({ Icon, title, body }) => (
            <div key={title} className="as-flex as-gap-4 as-items-start">
              <IconBadge Icon={Icon} />
              <div>
                <dt className="as-h3">{title}</dt>
                <dd className="as-p as-mt-2">{body}</dd>
              </div>
            </div>
          ))}
        </dl>
      </Container>
    </section>
  )
}

export const Conditions = () => {
  const list = [
    { Icon: CloudDrizzle, title: 'Depression' },
    { Icon: Waves, title: 'Anxiety' },
    { Icon: Zap, title: 'OCD' },
    { Icon: Activity, title: 'ADHD' },
    { Icon: Puzzle, title: 'Autism & developmental' },
    { Icon: HeartPulse, title: 'Behavioural challenges' },
  ]
  return (
    <section id="conditions" className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Conditions</Eyebrow>
          <SectionTitle>Support across clinical and behavioural concerns</SectionTitle>
          <Lede>
            Care pathways are structured by condition and adjusted to individual clinical
            presentation. Medical and endocrine review is included where relevant.
          </Lede>
        </div>
        <ul className="as-mt-12 as-grid as-grid-3-sm as-grid-3">
          {list.map(({ Icon, title }) => (
            <li key={title}>
              <Card className="as-flex as-items-center as-gap-3" >
                <IconBadge Icon={Icon} />
                <span className="as-h3">{title}</span>
              </Card>
            </li>
          ))}
        </ul>
        <div className="as-mt-8">
          <SecondaryButton to="/therapy-psychiatry">Explore care options</SecondaryButton>
        </div>
      </Container>
    </section>
  )
}

export const CareModel = () => {
  const steps = [
    { Icon: ClipboardList, title: 'Consultation', body: 'A trained clinician reviews what you\u2019re experiencing.' },
    { Icon: Brain, title: 'Assessment', body: 'Psychological, behavioural, and \u2014 when relevant \u2014 medical review.' },
    { Icon: Users, title: 'Therapy', body: 'A matched clinician begins your plan, with psychiatric or medical input added as needed.' },
    { Icon: Repeat, title: 'Ongoing care', body: 'Plans adjust over time; the care team stays with you.' },
  ]
  return (
    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Care model</Eyebrow>
          <SectionTitle>Built around continuity of care</SectionTitle>
          <Lede>
            Treatment may include therapy, psychiatric care and medication, assessments, and
            integrated clinical planning. Each step informs the next.
          </Lede>
        </div>
        <ol className="as-ordered-grid as-ordered-grid-4">
          {steps.map(({ Icon, title, body }, i) => (
            <li key={title}>
              <Card>
                <div className="as-step">
                  <IconBadge Icon={Icon} />
                  <span className="as-step-label">Step {i + 1}</span>
                </div>
                <h3 className="as-h3 as-mt-4">{title}</h3>
                <p className="as-p as-mt-2">{body}</p>
              </Card>
            </li>
          ))}
        </ol>
      </Container>
    </section>
  )
}

export const UnderstandingMH = () => {
  const cards = [
    { Icon: Brain, title: 'Psychological factors', body: 'Thoughts, emotions, beliefs, and lived experience. Addressed through evidence-informed therapy.' },
    { Icon: Activity, title: 'Behavioural patterns', body: 'Habits, sleep, relationships, and environment. The day-to-day shape of recovery.' },
    { Icon: Microscope, title: 'Biological systems', body: 'Sleep biology, hormones, and physiology can drive mood, anxiety, and cognition. Addressed by psychiatric and medical specialists when relevant.', emphasise: true },
  ]
  return (
    <section className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Beyond symptoms</Eyebrow>
          <SectionTitle>Mental health sits at the intersection of mind and body</SectionTitle>
          <Lede>
            Mental health concerns involve psychological factors, behavioural patterns, and
            biological systems. A full plan can look across all three.
          </Lede>
        </div>
        <div className="as-mt-12 as-grid as-grid-3">
          {cards.map(({ Icon, title, body, emphasise }) => (
            <Card key={title} emphasised={emphasise}>
              <IconBadge Icon={Icon} variant={emphasise ? 'violet' : 'slate'} />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </Card>
          ))}
        </div>
        <div className="as-callout">
          <div>
            <h3 className="as-h3">Why biology can matter for mental health</h3>
            <p className="as-p as-mt-2">
              Thyroid issues, hormonal shifts, insulin resistance, and sleep disorders can show
              up as depression, anxiety, brain fog, or low energy. Treating them alongside
              therapy often leads to more durable outcomes.
            </p>
          </div>
          <div className="as-mt-4">
            <Link to="/how-care-works" className="as-link as-link-violet">
              How mind&ndash;body care works <ArrowRight />
            </Link>
          </div>
        </div>
      </Container>
    </section>
  )
}

export const WhyIntegratedCare = () => {
  const included = [
    'Clinical intake and structured assessment',
    'Therapy matched to your presentation',
    'Psychiatric review when clinically indicated',
    'Endocrine and medical workup when relevant',
    'A shared clinical record across your care team',
    'Measurement-based tracking of progress',
    'Care coordination between providers',
    'Plan adjustments as your clinical picture changes',
  ]
  return (
    <section id="why-aumhum" className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>What you are choosing</Eyebrow>
          <SectionTitle>Structured clinical care — not a single session</SectionTitle>
          <Lede>
            Clinical care at AUMHUM is a coordinated system: evaluation, therapy, psychiatric
            review, and medical input — held together by continuity and a shared clinical view of
            your care.
          </Lede>
        </div>
        <div className="as-mt-10 as-grid-12">
          <div className="as-col-7">
            <Card>
              <h3 className="as-h3">Included in your care</h3>
              <ul className="as-check-list">
                {included.map((w) => (
                  <li key={w}>
                    <CheckCircle2 />
                    {w}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
          <div className="as-col-5">
            <p className="as-p">
              Care unfolds over time rather than one session at a time. That continuity is what
              makes clinical care effective — and is what you are choosing when you start a plan
              with us.
            </p>
            <p className="as-p as-mt-3">
              If your needs are simple and well-matched to a single conversation, many options
              exist. AUMHUM is built for everything beyond that — and for the many cases where
              the right answer is combined care.
            </p>
          </div>
        </div>
      </Container>
    </section>
  )
}

export const ContinuousCareSystem = () => {
  const items = [
    { Icon: Repeat, title: 'Longitudinal plans', body: 'Care unfolds over weeks and months — not one disconnected session at a time.' },
    { Icon: Workflow, title: 'Connected clinical record', body: 'Therapist, psychiatrist, and physician work from one shared view of your care.' },
    { Icon: Video, title: 'Between-session support', body: 'Check-ins, adjustments, and continuity of clinical attention — not silence between appointments.' },
    { Icon: LineChart, title: 'Measured outcomes', body: 'Validated clinical instruments track progress; the plan adapts to what is actually working.' },
    { Icon: Cpu, title: 'AI-assisted coordination', body: 'Clinical workflows augmented with AI to keep care coherent across providers, stages, and time. Designed for clinicians; safety-reviewed.' },
    { Icon: Zap, title: 'Adaptive pathways', body: 'Protocols that respond to measured change — so recovery is structured, not ad-hoc.' },
  ]
  return (
    <section className="as-section as-section-dark">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow dark>How it works</Eyebrow>
          <h2 className="as-h2" style={{ color: '#fff' }}>
            Continuous care, supported by modern clinical infrastructure
          </h2>
          <p className="as-lede">
            Traditional mental healthcare happens in fragments — sessions disconnected from each
            other and from medical context. AUMHUM is built as a continuous care system: a
            longitudinal plan, a shared clinical record, measured outcomes, and AI-assisted
            coordination that keeps your care coherent across providers and time.
          </p>
        </div>
        <ul className="as-mt-12 as-grid as-grid-3">
          {items.map(({ Icon, title, body }) => (
            <li key={title} className="as-card-dark">
              <IconBadge Icon={Icon} variant="dark" />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </li>
          ))}
        </ul>
        <div className="as-mt-10">
          <Link to="/how-care-works" className="as-btn as-btn-light">
            See how care works
            <ArrowRight />
          </Link>
        </div>
      </Container>
    </section>
  )
}

export const SelfCareCallout = () => (
  <section className="as-section as-section-alt" aria-labelledby="self-care-heading">
    <Container>
      <div className="as-grid-12 as-items-center">
        <div className="as-col-7">
          <Eyebrow>Also from AUMHUM</Eyebrow>
          <SectionTitle as="h2">
            <span id="self-care-heading">
              Prefer to start on your own? The AUMHUM self-care app is waiting.
            </span>
          </SectionTitle>
          <Lede>
            Assessments, guided meditations, journaling, breathwork, and evidence-informed
            courses — the same clinical foundation, delivered as a self-directed practice. Use
            it alongside clinical care, or as a first step.
          </Lede>
          <div className="as-mt-6 as-flex as-wrap as-gap-3">
            <PrimaryButton to="/self-care">Explore self-care</PrimaryButton>
            <SecondaryButton to="/assessment">Take an assessment</SecondaryButton>
          </div>
        </div>
        <div className="as-col-5">
          <Card>
            <div className="as-flex as-items-center as-gap-3">
              <IconBadge Icon={HeartPulse} />
              <p className="as-h3">Inside the self-care app</p>
            </div>
            <ul className="as-check-list" style={{ gridTemplateColumns: '1fr' }}>
              {[
                'Validated mental health assessments',
                'Guided meditations and breathwork',
                'Journaling prompts and tracking',
                'Evidence-informed courses and podcasts',
              ].map((w) => (
                <li key={w}>
                  <CheckCircle2 /> {w}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </Container>
  </section>
)

export const CareEnvironments = () => {
  const envs = [
    { Icon: Building2, title: 'Clinics', body: 'In-person care for assessment, therapy, and medical review.' },
    { Icon: MapPin, title: 'Rehab centres', body: 'For conditions requiring more sustained or intensive support.' },
    { Icon: Video, title: 'Remote & hybrid', body: 'Secure teleconsultation with the same care team across settings.' },
  ]
  return (
    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Care environments</Eyebrow>
          <SectionTitle>Care that follows you, across settings</SectionTitle>
          <Lede>
            Care is delivered wherever it fits best &mdash; in-person, in rehab, or remotely.
            Your plan and care team move with you.
          </Lede>
        </div>
        <div className="as-mt-12 as-grid as-grid-3">
          {envs.map(({ Icon, title, body }) => (
            <Card key={title}>
              <IconBadge Icon={Icon} />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </Card>
          ))}
        </div>
      </Container>
    </section>
  )
}

// Kept around for any future single-stakeholder use; the homepage now renders
// WorkWithAumhum (a single 3-column section) instead.
const BulletBand = ({ id, eyebrow, title, lede, bullets, cta, alt = false }) => (
  <section id={id} className={`as-section ${alt ? 'as-section-alt' : 'as-section-white'}`}>
    <Container>
      <div className="as-grid-12">
        <div className="as-col-5">
          <Eyebrow>{eyebrow}</Eyebrow>
          <SectionTitle>{title}</SectionTitle>
          <Lede>{lede}</Lede>
          <div className="as-mt-6">
            <PrimaryButton to={cta.to}>{cta.label}</PrimaryButton>
          </div>
        </div>
        <ul className="as-col-7 as-bullet-cards" style={{ marginTop: 0 }}>
          {bullets.map((b) => (
            <li key={b} className="as-bullet-card">
              <CheckCircle2 /> {b}
            </li>
          ))}
        </ul>
      </div>
    </Container>
  </section>
)

export const ForOrganizations = () => (
  <BulletBand
    id="for-orgs"
    eyebrow="For organizations"
    title="Structured mental healthcare for your workforce"
    lede="AUMHUM works with organizations to provide clinical mental healthcare for employees \u2014 beyond transactional EAP sessions."
    bullets={[
      'Therapy access across your teams',
      'Clinical consultations for complex cases',
      'Psychiatric and medical integration',
      'Endocrine review where clinically relevant',
      'Confidential outcomes reporting for HR',
      'Support across geographies',
    ]}
    cta={{ label: 'Partner with us', to: '/aumhum-for-workplace' }}
    alt
  />
)

export const ForClinics = () => (
  <BulletBand
    id="for-clinics"
    eyebrow="For clinics & rehab centres"
    title="Extend and strengthen mental healthcare services"
    lede="Enable evidence-informed therapy, psychiatric coordination, and continuity of care within your setting."
    bullets={[
      'Add evidence-informed therapy programs',
      'Integrate psychiatric review',
      'Coordinate endocrine and medical input',
      'Shared clinical record across your care team',
      'Outcome measurement and reporting',
      'Continuity with outpatient and remote care',
    ]}
    cta={{ label: 'Partner with us', to: '/aumhum-for-clinics' }}
  />
)

export const ForPsychologists = () => (
  <BulletBand
    id="for-psychologists"
    eyebrow="For psychologists"
    title="Build your clinical practice with real-world experience"
    lede="Work across diverse clinical settings with evidence-informed therapy protocols, psychiatric collaboration, and exposure to varied case profiles."
    bullets={[
      'Work across clinical environments',
      'Exposure to varied case profiles',
      'Evidence-informed therapy systems',
      'Psychiatric and medical collaboration',
      'Supervision and continuing development',
      'Opportunities across regions',
    ]}
    cta={{ label: 'Join our network', to: '/mentors/join' }}
    alt
  />
)

// ────────────────────────────────────────────────────────────
// WORK WITH AUMHUM \u2014 single section, 3 stakeholder cards
// ────────────────────────────────────────────────────────────

const STAKEHOLDERS = [
  {
    Icon: Building2,
    title: 'Organizations',
    intro: 'Mental healthcare for your workforce, beyond transactional EAP sessions.',
    bullets: [
      'Therapy access across teams',
      'Clinical consultations for complex cases',
      'Psychiatric and medical integration',
      'Confidential outcomes reporting',
    ],
    cta: { label: 'Partner with us', to: '/aumhum-for-workplace' },
  },
  {
    Icon: Stethoscope,
    title: 'Clinics & rehab',
    intro: 'Extend therapy, psychiatric coordination, and continuity of care inside your clinic.',
    bullets: [
      'Therapy systems',
      'Psychiatric coordination',
      'Continuity of care',
      'Outcome measurement',
    ],
    cta: { label: 'Partner with us', to: '/aumhum-for-clinics' },
  },
  {
    Icon: GraduationCap,
    title: 'Psychologists',
    intro: 'Real-world clinical exposure inside an integrated care team \u2014 not a directory.',
    bullets: [
      'Real-world exposure',
      'Evidence-informed protocols',
      'Diverse case profiles',
      'Supervision & development',
    ],
    cta: { label: 'Join our network', to: '/mentors/join' },
  },
]

export const WorkWithAumhum = () => (
  <section className="as-section as-section-alt" aria-labelledby="work-with-heading">
    <Container>
      <div className="as-max-w-3">
        <Eyebrow>Work with AUMHUM</Eyebrow>
        <SectionTitle>
          <span id="work-with-heading">Partners, clinicians, and clinics</span>
        </SectionTitle>
        <Lede>
          AUMHUM works alongside the organizations, clinics, and clinicians who deliver
          mental healthcare on the ground. Three ways to plug in.
        </Lede>
      </div>
      <ul className="as-mt-12 as-grid as-grid-3">
        {STAKEHOLDERS.map(({ Icon, title, intro, bullets, cta }) => (
          <li key={title}>
            <Card className="as-stakeholder">
              <div className="as-flex as-items-center as-gap-3">
                <IconBadge Icon={Icon} />
                <h3 className="as-h3">{title}</h3>
              </div>
              <p className="as-p">{intro}</p>
              <ul>
                {bullets.map((b) => (
                  <li key={b}>
                    <CheckCircle2 /> {b}
                  </li>
                ))}
              </ul>
              <div className="as-stakeholder-cta">
                <Link to={cta.to} className="as-link as-link-violet">
                  {cta.label} <ArrowRight />
                </Link>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </Container>
  </section>
)

export const WhyAumhumSummary = () => {
  const points = [
    { Icon: Workflow, title: 'Care that connects across stages', body: 'Consultation, therapy, psychiatric input, and follow-up move together \u2014 not as separate appointments.' },
    { Icon: Repeat, title: 'Continuity-focused', body: 'Plans live across visits, not just inside one. Providers can change; the plan does not restart.' },
    { Icon: Microscope, title: 'Therapy + psychiatry + biology', body: 'Care brings in psychiatric and biological context only when it actually helps the plan.' },
    { Icon: Users, title: 'Supports patients and clinicians', body: 'Designed for both sides of the room. The same clinical view powers patient care and clinician workflows.' },
  ]
  return (
    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Why AUMHUM</Eyebrow>
          <SectionTitle>Care that connects, not a directory</SectionTitle>
          <Lede>
            Most mental health services start and end at booking. AUMHUM is built around what
            happens after &mdash; the plan, the follow-through, and the people who carry it out.
          </Lede>
        </div>
        <div className="as-mt-12 as-grid as-grid-2">
          {points.map(({ Icon, title, body }) => (
            <Card key={title}>
              <IconBadge Icon={Icon} />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </Card>
          ))}
        </div>
      </Container>
    </section>
  )
}

export const Impact = () => (
  <section className="as-section as-section-white">
    <Container>
      <div className="as-grid-12 as-items-center">
        <div className="as-col-7">
          <Eyebrow>Impact</Eyebrow>
          <SectionTitle>Expanding access to mental healthcare across regions</SectionTitle>
          <Lede>
            Building pathways that make mental healthcare more accessible, more consistent, and
            more integrated into everyday care settings.
          </Lede>
        </div>
        <div className="as-col-5">
          <Card>
            <div className="as-flex as-items-center as-gap-3">
              <IconBadge Icon={BookOpenCheck} />
              <p className="as-h3">Clinical governance</p>
            </div>
            <p className="as-p as-mt-3">
              Care is delivered by licensed clinicians under clinical supervision, with
              documented protocols, privacy safeguards, and outcome measurement built in.
            </p>
          </Card>
        </div>
      </div>
    </Container>
  </section>
)

export const FinalCTA = () => (
  <section id="get-help" className="as-cta-final">
    <Container>
      <div className="as-grid-12 as-items-center">
        <div className="as-col-7">
          <Eyebrow dark>Start your care journey</Eyebrow>
          <h2 className="as-h2" style={{ color: '#fff' }}>
            One step is enough to start.
          </h2>
          <p className="as-lede">
            Whether you&rsquo;re looking, deciding, or ready &mdash; pick the path that fits.
            Our team takes it from there.
          </p>
        </div>
        <div className="as-col-5 as-flex as-wrap as-gap-3 as-justify-end">
          <Link to="/care/start" className="as-btn as-btn-light">
            Get help <ArrowRight />
          </Link>
          <Link to="/therapy-psychiatry?t=psychiatrist" className="as-btn as-btn-ghost-on-dark">
            Book consultation
          </Link>
          <Link to="/therapy-psychiatry?t=therapist" className="as-btn as-btn-ghost-on-dark">
            Start therapy
          </Link>
        </div>
      </div>
    </Container>
  </section>
)

// ────────────────────────────────────────────────────────────
// HOW CARE WORKS
// ────────────────────────────────────────────────────────────

export const HowHero = () => (
  <section className="as-section-white" style={{ borderBottom: '1px solid var(--slate-200)' }}>
    <Container>
      <div style={{ padding: '5rem 0' }}>
        <Eyebrow>How care works</Eyebrow>
        <h1 className="as-h1 as-max-w-4">
          A continuous clinical system — not a directory of sessions.
        </h1>
        <p className="as-lede">
          What happens when you start care at AUMHUM, who is on your care team, how psychiatric
          and endocrine input are integrated, and what our clinical infrastructure does in the
          background to keep your care coherent.
        </p>
        <div className="as-mt-8 as-flex as-wrap as-items-center as-gap-3">
          <PrimaryButton to="/therapy-psychiatry">Book consultation</PrimaryButton>
          <SecondaryButton to="/">Back to home</SecondaryButton>
        </div>
      </div>
    </Container>
  </section>
)

export const HowPathway = () => {
  const steps = [
    { Icon: ClipboardList, title: 'Clinical intake', body: 'A trained clinical evaluator reviews your presenting concerns, history, and context. The goal is clarity — not a sales call.' },
    { Icon: Brain, title: 'Structured assessment', body: 'Standardised psychological, behavioural, and — where indicated — medical assessments. For conditions with a biological signature, endocrine workup is recommended.' },
    { Icon: Users, title: 'Care plan and matching', body: 'You are matched to a therapist aligned with your clinical needs. If appropriate, a psychiatrist and/or endocrinologist enters the plan.' },
    { Icon: Repeat, title: 'Therapy + specialist input', body: 'Structured therapy sessions, psychiatric reviews, and medical follow-ups move in parallel. The care team coordinates internally.' },
    { Icon: LineChart, title: 'Measurement', body: 'Progress is tracked with validated clinical instruments. Measurement is used to adjust the plan — not to gate access.' },
    { Icon: Workflow, title: 'Ongoing continuity', body: 'Your plan is revisited. Providers can change; continuity of the plan does not.' },
  ]
  return (
    <section className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>The pathway</Eyebrow>
          <SectionTitle>What the clinical journey looks like</SectionTitle>
          <Lede>
            Each step has a clinical purpose. The pathway is structured; every plan is personalised.
          </Lede>
        </div>
        <ol className="as-ordered-grid as-ordered-grid-2">
          {steps.map(({ Icon, title, body }, i) => (
            <li key={title}>
              <Card>
                <div className="as-step">
                  <IconBadge Icon={Icon} />
                  <span className="as-step-label">Step {i + 1}</span>
                </div>
                <h3 className="as-h3 as-mt-4">{title}</h3>
                <p className="as-p as-mt-2">{body}</p>
              </Card>
            </li>
          ))}
        </ol>
      </Container>
    </section>
  )
}

export const HowTeam = () => {
  const team = [
    { Icon: Users, title: 'Therapist', body: 'Licensed psychologist delivering structured therapy matched to your clinical presentation.' },
    { Icon: Stethoscope, title: 'Psychiatrist', body: 'Involved when diagnostic clarity, medication review, or psychiatric oversight is clinically indicated.' },
    { Icon: Microscope, title: 'Endocrinologist / physician', body: 'Brought in when symptoms suggest a hormonal, metabolic, or medical driver — thyroid, PCOS, cortisol, sleep disorders, and more.' },
    { Icon: Workflow, title: 'Clinical coordinator', body: 'Keeps providers aligned, supports continuity, and ensures nothing falls between the cracks.' },
  ]
  return (
    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Your care team</Eyebrow>
          <SectionTitle>Who you work with</SectionTitle>
          <Lede>
            AUMHUM is not one therapist at a time. It is a care team matched to your clinical
            picture — and scaled up or down as needed.
          </Lede>
        </div>
        <div className="as-mt-12 as-grid as-grid-2">
          {team.map(({ Icon, title, body }) => (
            <Card key={title}>
              <IconBadge Icon={Icon} />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </Card>
          ))}
        </div>
      </Container>
    </section>
  )
}

export const HowEndocrine = () => {
  const examples = [
    { title: 'Thyroid disorders', body: 'Hypo- and hyperthyroidism can present as depression, anxiety, fatigue, or cognitive complaints. Untreated, therapy progress stalls.' },
    { title: 'PCOS and reproductive hormones', body: 'Hormonal variation through the cycle, perimenopause, and PCOS can drive mood and anxiety symptoms that respond to combined care.' },
    { title: 'Insulin & metabolic health', body: 'Metabolic dysregulation and insulin resistance interact with mood, energy, and cognitive function.' },
    { title: 'Cortisol and stress physiology', body: 'Chronic stress physiology influences sleep, mood, and anxiety — and sometimes requires medical as well as behavioural intervention.' },
    { title: 'Sleep disorders', body: 'Sleep-disordered breathing, circadian disruption, and sleep-related hormonal changes can mimic or worsen psychiatric conditions.' },
    { title: 'Testosterone and androgens', body: 'Low or dysregulated androgens are associated with mood, motivation, and cognitive changes — particularly across the lifespan.' },
  ]
  return (
    <section className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Mind–body care</Eyebrow>
          <SectionTitle>When endocrine evaluation becomes part of mental healthcare</SectionTitle>
          <Lede>
            Mental health and endocrine health are biologically entangled. Symptoms that look
            purely psychological often have measurable biological drivers. Treating both produces
            better, more durable outcomes than treating either alone.
          </Lede>
        </div>
        <div className="as-mt-12 as-grid as-grid-3">
          {examples.map(({ title, body }) => (
            <Card key={title}>
              <h3 className="as-h3">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </Card>
          ))}
        </div>
        <div className="as-callout">
          <p className="as-p">
            Endocrine workup is recommended when the clinical picture suggests it — not as a
            default. When indicated, we bring an endocrinologist into your care plan and
            coordinate with your therapist and psychiatrist.
          </p>
        </div>
      </Container>
    </section>
  )
}

export const HowInfrastructure = () => {
  const items = [
    { Icon: Workflow, title: 'Shared clinical record', body: 'Every provider works from one clinical view — consultations, assessments, therapy notes, prescriptions, and lab results.' },
    { Icon: LineChart, title: 'Measurement-based care', body: 'Validated instruments track symptom change over time, so decisions are informed by data rather than impression.' },
    { Icon: Cpu, title: 'AI-assisted coordination', body: 'Clinical workflows are augmented by AI — summarising context, surfacing patterns, flagging continuity risks, and supporting clinicians between sessions. AI supports clinicians; it does not replace them.' },
    { Icon: Zap, title: 'Adaptive protocols', body: 'Treatment plans adjust as the clinical picture changes. Protocols are updated as the evidence evolves.' },
    { Icon: ShieldCheck, title: 'Privacy and governance', body: 'Clinical data is protected, access-controlled, and handled under documented clinical governance.' },
    { Icon: Repeat, title: 'Continuity built-in', body: 'If a provider changes, the plan does not restart. The clinical record and care team structure carry forward.' },
  ]
  return (
    <section className="as-section as-section-dark">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow dark>Clinical infrastructure</Eyebrow>
          <h2 className="as-h2" style={{ color: '#fff' }}>
            What the system does in the background
          </h2>
          <p className="as-lede">
            Continuous care is only possible because of clinical infrastructure built to support
            it: a shared record, measurement, adaptive protocols, and AI-assisted coordination
            that keeps providers aligned across time.
          </p>
        </div>
        <div className="as-mt-12 as-grid as-grid-3">
          {items.map(({ Icon, title, body }) => (
            <div key={title} className="as-card-dark">
              <IconBadge Icon={Icon} variant="dark" />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}

export const HowValue = () => {
  const whatsIncluded = [
    'Clinical intake and structured assessment',
    'Matching with a therapist aligned to your presentation',
    'Access to psychiatric review when clinically indicated',
    'Access to endocrine and medical workup when relevant',
    'Shared clinical record across every provider',
    'Measurement-based tracking of progress',
    'Between-session continuity and care coordination',
    'Plan adjustments as your clinical picture changes',
  ]
  return (
    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>What is included</Eyebrow>
          <SectionTitle>A care plan, not a single appointment</SectionTitle>
          <Lede>
            Clinical care at AUMHUM is structured to include what continuous care actually
            requires — evaluation, coordination, medical context where relevant, and
            follow-through over time.
          </Lede>
        </div>
        <div className="as-mt-10 as-grid-12">
          <div className="as-col-7">
            <Card>
              <h3 className="as-h3">Included in every care plan</h3>
              <ul className="as-check-list">
                {whatsIncluded.map((w) => (
                  <li key={w}>
                    <CheckCircle2 />
                    {w}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
          <div className="as-col-5">
            <p className="as-p">
              Clinical governance, supervision, and privacy are built into the care model — not
              offered as an add-on.
            </p>
            <p className="as-p as-mt-3">
              If your needs are simple and well-matched to a single conversation, many options
              exist. AUMHUM is built for everything beyond that — and for the many cases where
              the right answer is combined care.
            </p>
          </div>
        </div>
      </Container>
    </section>
  )
}

export const HowFaq = () => {
  const faqs = [
    {
      q: 'Do I have to see a psychiatrist or an endocrinologist?',
      a: 'No. These specialists are brought into your plan only when clinically indicated. Many people are supported entirely by therapy. The point of continuous care is that specialist input is available, coordinated, and seamless if and when it is needed.',
    },
    {
      q: 'Is this remote or in-person?',
      a: 'Both. Care is delivered across outpatient clinics, rehabilitation settings, and secure teleconsultation. Your care team and plan stay consistent across settings.',
    },
    {
      q: 'How does AI fit into clinical care?',
      a: 'AI is used to support clinicians — summarising clinical context, surfacing longitudinal patterns, flagging continuity gaps, and helping coordinate between providers. It is reviewed, governed, and never replaces clinical judgement.',
    },
    {
      q: 'How is my clinical data protected?',
      a: 'Clinical data is protected under documented privacy and governance standards. Access is restricted to your care team, and you retain the right to review and control your record.',
    },
    {
      q: 'How do I start?',
      a: 'Begin with a consultation. Your evaluator will help you understand what kind of care is appropriate — therapy alone, combined therapy and psychiatric review, or care that includes medical and endocrine workup.',
    },
  ]
  return (
    <section className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Common questions</Eyebrow>
          <SectionTitle>What people ask before starting</SectionTitle>
        </div>
        <dl className="as-mt-10 as-grid as-grid-2">
          {faqs.map(({ q, a }) => (
            <Card key={q}>
              <dt className="as-h3">{q}</dt>
              <dd className="as-p as-mt-2">{a}</dd>
            </Card>
          ))}
        </dl>
        <div className="as-mt-10">
          <PrimaryButton to="/therapy-psychiatry">Book a consultation</PrimaryButton>
        </div>
      </Container>
    </section>
  )
}

// ────────────────────────────────────────────────────────────
// STUDENTS
// ────────────────────────────────────────────────────────────

export const StudentsProgram = () => (
  <>
    <section className="as-section-white" style={{ borderBottom: '1px solid var(--slate-200)' }}>
      <Container>
        <div style={{ padding: '5rem 0' }}>
          <Eyebrow>Experience Program</Eyebrow>
          <h1 className="as-h1 as-max-w-4">
            AUMHUM Experience Program — a 6-month clinical placement for psychology students.
          </h1>
          <p className="as-lede">
            Real patient exposure, structured supervision, and growth across AUMHUM&rsquo;s clinical
            network. Designed for psychology students at every stage — from undergraduate to
            post-graduate and doctoral-track trainees.
          </p>
          <div className="as-mt-8 as-flex as-wrap as-items-center as-gap-3">
            <PrimaryButton href="mailto:namaste@aumhum.com?subject=AUMHUM%20Experience%20Program%20Application">
              Apply to the program
            </PrimaryButton>
            <SecondaryButton to="/">Back to home</SecondaryButton>
          </div>
        </div>
      </Container>
    </section>

    <section className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Who this is for</Eyebrow>
          <SectionTitle>Psychology students at every stage</SectionTitle>
          <Lede>
            Undergraduate, post-graduate, MPhil, PhD, and clinical psychology trainees — anyone
            studying psychology and serious about becoming a practising clinician.
          </Lede>
        </div>
        <ul className="as-mt-10 as-bullet-cards">
          {[
            'Undergraduate psychology students seeking early clinical exposure',
            'Post-graduate students pursuing counselling or clinical psychology',
            'MPhil and PhD clinical psychology trainees',
            'Students exploring whether clinical practice is the right career',
          ].map((s) => (
            <li key={s} className="as-bullet-card">
              <GraduationCap /> {s}
            </li>
          ))}
        </ul>
      </Container>
    </section>

    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>What you get</Eyebrow>
          <SectionTitle>A 6-month clinical experience you cannot get in a classroom</SectionTitle>
          <Lede>
            Graduate psychology programmes often do not expose students to real patients or to
            integrated multi-disciplinary care. The AUMHUM Experience Program is designed to close
            that gap.
          </Lede>
        </div>
        <div className="as-mt-10 as-grid as-grid-3">
          {[
            { Icon: Stethoscope, title: 'Real patient exposure', body: 'Participate in intake, screenings, and follow-up with real patients — under direct clinical supervision throughout.' },
            { Icon: ClipboardList, title: 'Administer validated scales', body: 'Learn to administer and score standardised clinical instruments — PHQ-9, GAD-7, Y-BOCS, ADHD rating scales, and more.' },
            { Icon: UserCheck, title: 'Supervision by senior clinicians', body: 'Structured supervision by psychiatry and psychology leaders — with case reviews, feedback, and clinical mentorship.' },
            { Icon: Building2, title: 'Rotation across clinical settings', body: 'Exposure to outpatient clinics, rehabilitation environments, and remote care — building breadth of clinical understanding.' },
            { Icon: Workflow, title: 'Inside a continuous care system', body: 'See how integrated clinical care actually works — shared clinical records, measurement, and multi-disciplinary coordination.' },
            { Icon: Cpu, title: 'Modern clinical tooling', body: "Work with the measurement-based and AI-assisted coordination tools used by AUMHUM's practising clinicians." },
          ].map(({ Icon, title, body }) => (
            <Card key={title}>
              <IconBadge Icon={Icon} />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </Card>
          ))}
        </div>
      </Container>
    </section>

    <section className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>The program</Eyebrow>
          <SectionTitle>Structured across six months</SectionTitle>
          <Lede>
            Each phase has clear learning objectives, supervised responsibilities, and measurable
            clinical competencies.
          </Lede>
        </div>
        <ol className="as-ordered-grid as-ordered-grid-3">
          {[
            { period: 'Months 1–2', title: 'Foundations', body: 'Onboarding, clinical orientation, observation, and scoring practice with senior clinicians. Learn the structured intake process and validated instruments.' },
            { period: 'Months 3–4', title: 'Supervised practice', body: 'Begin administering screenings and scales with patients under direct supervision. Participate in case reviews. Contribute to care coordination.' },
            { period: 'Months 5–6', title: 'Clinical development', body: 'Expanded responsibility under supervision. Exposure to complex multi-disciplinary cases. Structured feedback reviews and a clinical growth report at completion.' },
          ].map(({ period, title, body }) => (
            <li key={period}>
              <Card>
                <div className="as-period">{period}</div>
                <h3 className="as-h3 as-mt-3">{title}</h3>
                <p className="as-p as-mt-2">{body}</p>
              </Card>
            </li>
          ))}
        </ol>
      </Container>
    </section>

    <FinalCTA />
  </>
)

// ────────────────────────────────────────────────────────────
// ROLE PAGE
// ────────────────────────────────────────────────────────────

// Abstract, non-photo clinical visual for the RolePage hero — avoids the
// directory / marketplace feel of stock-photo portraits while still filling
// the right-hand column with something visually rich. Uses the scoped
// AumhumSite CSS tokens for colour so it stays on-brand.
const RoleHeroVisual = ({ Icon }) => (
  <div className="as-role-visual" aria-hidden="true">
    <svg
      viewBox="0 0 400 320"
      xmlns="http://www.w3.org/2000/svg"
      className="as-role-visual__bg"
      role="presentation"
    >
      <defs>
        <linearGradient id="as-role-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ede9fe" />
          <stop offset="100%" stopColor="#eef2ff" />
        </linearGradient>
        <radialGradient id="as-role-dot" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#c4b5fd" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="320" rx="16" fill="url(#as-role-grad)" />
      <g opacity="0.5">
        <circle cx="320" cy="70" r="80" fill="url(#as-role-dot)" />
        <circle cx="60" cy="260" r="100" fill="url(#as-role-dot)" />
      </g>
      {/* Decorative pulse / waveform */}
      <path
        d="M 20 160 L 90 160 L 110 120 L 140 200 L 170 100 L 200 220 L 230 140 L 260 180 L 300 160 L 380 160"
        stroke="#8b5cf6"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.7"
      />
      {/* Network / coordination motif */}
      <g fill="#7c3aed" opacity="0.85">
        <circle cx="80" cy="80" r="5" />
        <circle cx="180" cy="50" r="5" />
        <circle cx="280" cy="90" r="5" />
        <circle cx="330" cy="200" r="5" />
        <circle cx="220" cy="250" r="5" />
        <circle cx="110" cy="230" r="5" />
      </g>
      <g stroke="#a78bfa" strokeWidth="1.2" opacity="0.55" fill="none">
        <line x1="80" y1="80" x2="180" y2="50" />
        <line x1="180" y1="50" x2="280" y2="90" />
        <line x1="280" y1="90" x2="330" y2="200" />
        <line x1="330" y1="200" x2="220" y2="250" />
        <line x1="220" y1="250" x2="110" y2="230" />
        <line x1="110" y1="230" x2="80" y2="80" />
        <line x1="180" y1="50" x2="220" y2="250" />
        <line x1="80" y1="80" x2="280" y2="90" />
      </g>
    </svg>
    <div className="as-role-visual__badge">
      <Icon />
    </div>
  </div>
)

export const RolePage = ({ config }) => (
  <>
    <section className="as-hero" aria-labelledby="role-hero-title">
      <Container>
        <div className="as-hero-grid">
          <div>
            <Eyebrow>{config.group}</Eyebrow>
            <div className="as-flex as-wrap as-items-center as-gap-4 as-mt-2">
              <IconBadge Icon={config.Icon} />
              <h1 id="role-hero-title" className="as-h1">{config.title}</h1>
            </div>
            <p className="as-lede">{config.tagline}</p>
            <div className="as-mt-8 as-flex as-wrap as-items-center as-gap-3">
              <PrimaryButton to={config.cta.href}>{config.cta.label}</PrimaryButton>
              <SecondaryButton to="/how-care-works">How care works</SecondaryButton>
              <Link to="/therapy-psychiatry" className="as-link">
                Looking for care instead? →
              </Link>
            </div>
            <ul className="as-mt-8 as-flex as-wrap as-gap-6" aria-label="Role signals">
              <li className="as-trust-item">
                <ShieldCheck /> Licensed &amp; supervised
              </li>
              <li className="as-trust-item">
                <Workflow /> Integrated care team
              </li>
              <li className="as-trust-item">
                <Repeat /> Continuity by default
              </li>
            </ul>
          </div>
          <div>
            <RoleHeroVisual Icon={config.Icon} />
          </div>
        </div>
      </Container>
    </section>

    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>About the role</Eyebrow>
          <SectionTitle>Working at AUMHUM</SectionTitle>
          <Lede>{config.intro}</Lede>
        </div>
      </Container>
    </section>

    <section className="as-section as-section-alt">
      <Container>
        <div className="as-grid-12">
          <div className="as-col-6">
            <Eyebrow>Day-to-day</Eyebrow>
            <SectionTitle>What the role involves</SectionTitle>
            <ul className="as-mt-6" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
              {config.role.map((r) => (
                <li key={r} className="as-check-card">
                  <CheckCircle2 /> {r}
                </li>
              ))}
            </ul>
          </div>
          <div className="as-col-6">
            <Eyebrow>Collaboration</Eyebrow>
            <SectionTitle>Who you work with</SectionTitle>
            <p className="as-p as-mt-6">{config.collaboration}</p>
          </div>
        </div>
      </Container>
    </section>

    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>What we offer</Eyebrow>
          <SectionTitle>Why clinicians join AUMHUM</SectionTitle>
        </div>
        <ul className="as-bullet-cards">
          {config.offer.map((o) => (
            <li key={o} className="as-bullet-card">
              <CheckCircle2 /> {o}
            </li>
          ))}
        </ul>
      </Container>
    </section>

    <section className="as-cta-final">
      <Container>
        <div className="as-grid-12 as-items-center">
          <div className="as-col-8">
            <Eyebrow dark>{config.cta.label}</Eyebrow>
            <h2 className="as-h2" style={{ color: '#fff' }}>
              {config.title} — inside a clinical system.
            </h2>
            <p className="as-lede">
              Share your profile and we will reach out within five working days.
            </p>
          </div>
          <div className="as-col-4 as-flex as-wrap as-gap-3 as-justify-end">
            <Link to={config.cta.href} className="as-btn as-btn-light">
              {config.cta.label} <ArrowRight />
            </Link>
          </div>
        </div>
      </Container>
    </section>
  </>
)
